import { Component, Injector, OnInit } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { CONFIG_CONTEXT } from 'src/app/enum/config-context.enum';
import { StatisticTypeEnum } from 'src/app/enum/statistic-type.enum';
import {
  BaseValidationProjectInfoModel,
  BaseValidationSideBarActionsModel,
} from 'src/app/models/configurators-2d/applications/kitchen/validation/base-validation-project-info';
import { KitchenPricing } from 'src/app/models/configurators-2d/applications/kitchen/kitchen-configuration-price';

import {
  InspirationModel,
  KitchenValidationProjectInfoModel,
  KitchenValidationSideBarActionsModel,
} from 'src/app/models/configurators-2d/applications/kitchen/validation/kitchen-validation-models';
import {
  StorageValidationProjectInfoModel,
  StorageValidationProjectResumeModel,
  StorageValidationSideBarActionsModel,
} from 'src/app/models/configurators-2d/applications/storage/validation/storage-validation-models';
import { KitchenService } from 'src/app/services/configurators-2d/applications/kitchen.service';
import { CurrentService } from 'src/app/services/current.service';
import { CustomerService } from 'src/app/services/customer.service';
import { DatasService } from 'src/app/services/datas.service';
import { Customer } from 'src/app/services/kingfisher/customer';
import { ProjectService } from 'src/app/services/project.service';
import { StatisticService } from 'src/app/services/statistic.service';
import { TunnelService } from 'src/app/services/tunnel.service';
import { InstallationInput } from '../../models/configurators-2d/tunnel/kitchen/kitchen-tunnel-store-model';
import { logError } from 'src/app/services/helpers/sentry.helper';

export enum VALIDATION_COMPONENT_TYPE_ENUM {
  VALIDATION_PROJECT_INFO,
  VALIDATION_PROJECT_RESUME,
  VALIDATION_SIDE_BAR_ACTION,
  ALL,
}

@Component({
  selector: 'ng-casto-vendor-validation',
  templateUrl: './validation.component.html',
  host: { class: 'container' },
})
export class VendorValidationComponent implements OnInit {
  public readonly validationProjectInfo: BehaviorSubject<BaseValidationProjectInfoModel> =
    new BehaviorSubject<BaseValidationProjectInfoModel>(undefined);
  public readonly validationProjectInfoLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public readonly validationProjectResume: BehaviorSubject<StorageValidationProjectResumeModel> =
    new BehaviorSubject<StorageValidationProjectResumeModel>(undefined);
  public readonly validationProjectResumeLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public readonly validationSideBarActions: BehaviorSubject<BaseValidationSideBarActionsModel> =
    new BehaviorSubject<BaseValidationSideBarActionsModel>(undefined);
  public readonly validationSideBarActionsLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  get priceKey() {
    const tunnelService = this.injector.get(TunnelService);
    return tunnelService?.kitchenTunnelData?.isLess2Years ? 'moins2' : 'plus2';
  }

  get currentContext(): string {
    return this.injector.get(CustomerService).context;
  }

  get isCurrentContextKitchen(): boolean {
    return this.currentContext === CONFIG_CONTEXT.KITCHEN;
  }

  get isCurrentContextStorage(): boolean {
    return this.currentContext === CONFIG_CONTEXT.STORAGE;
  }

  get beautifiedBom() {
    const tunnelService = this.injector.get(TunnelService);
    if (this.isCurrentContextKitchen) {
      return tunnelService?.kitchenTunnelData?.bom;
    } else if (this.isCurrentContextStorage) {
      return tunnelService?.storageTunnelData?.bom;
    }
    return false;
  }

  private readonly projectImgUrl: string[] = [];

  constructor(private readonly injector: Injector) {}

  ngOnInit() {
    const currentService = this.injector.get(CurrentService);
    
    if (!currentService.HBMProject) {
      logError('Erreur : projet non chargé');
      return;
    }

    this.initProjectResume()
      .then(() => {
        this.initProjectInfo()
          .then(() => this.initSideBarActions());
    });

    this.sendProjectValidationCountStatistic();
  }

  sendProjectValidationCountStatistic() {
    const customerService = this.injector.get(CustomerService);

    this.injector
      .get(StatisticService)
      .sendStatistic(StatisticTypeEnum.VALIDATION_PER_PROJECT_COUNT, 1, customerService.context);
  }

  get errorValidation() {
    const tunnelService = this.injector.get(TunnelService);
    if (
      this.isCurrentContextStorage &&
      tunnelService.storageTunnelData.downPayment == 0 &&
      tunnelService.storageTunnelData.mensuality == 0
    ) {
      return true;
    }

    return false;
  }

  private initSideBarActions() {
    if (this.isCurrentContextKitchen) {
      this.buildKitchenValidationSideBarActions();
    } else if (this.isCurrentContextStorage) {
      this.buildStorageValidationSideBarActions();
    }
  }

  private buildInspirationModel(): InspirationModel {
    const currentService = this.injector.get(CurrentService);
    const datasService = this.injector.get(DatasService);

    let currentModel: string;
    if (Number(currentService.kitchen.modelId) > 0) {
      currentModel = datasService.models.find(m => m.id === currentService.kitchen.modelId).title;
    }

    let currentLayout: string;
    if (currentService.kitchen.layoutRef) {
      currentLayout = datasService.layouts.find(l => l.id === currentService.kitchen.layoutRef).titleRef;
    }

    let currentOvenConfiguration: string;
    if (currentService.kitchen.ovenConfigurationRef) {
      currentOvenConfiguration = datasService.ovenConfigurations.find(
        oc => oc.id === currentService.kitchen.ovenConfigurationRef
      ).titleRef;
    }

    let currentFridgeConfiguration: string;
    if (currentService.kitchen.fridgeConfigurationRef) {
      currentFridgeConfiguration = datasService.fridgeConfigurations.find(
        fc => fc.id === currentService.kitchen.fridgeConfigurationRef
      ).titleRef;
    }

    return {
      currentModel: currentModel,
      currentLayout: currentLayout,
      currentFridgeConfiguration: currentFridgeConfiguration,
      currentOvenConfiguration: currentOvenConfiguration,
    } as InspirationModel;
  }

  private buildKitchenValidationSideBarActions() {
    const currentService = this.injector.get(CurrentService);
    let productList = this.buildProductList();
    let inspiration = this.buildInspirationModel();
    const validationSideBarActions: KitchenValidationSideBarActionsModel = {
      productList: productList,
      locked: currentService.HBMProject.locked,
      inspiration: inspiration,
      projectImageUrl: this.projectImgUrl,
    };

    this.validationSideBarActions.next(validationSideBarActions);
  }

  private buildStorageValidationSideBarActions() {
    const currentService = this.injector.get(CurrentService);
    let productList = this.buildProductList();
    const validationSideBarActions: StorageValidationSideBarActionsModel = {
      productList: productList,
      locked: currentService.HBMProject.locked,
      projectImageUrl: this.projectImgUrl,
    };

    this.validationSideBarActions.next(validationSideBarActions);
  }

  private async initProjectResume() {
    this.validationProjectResumeLoading.next(false);

    this.buildValidationProjectResumeModel();
  }

  private buildValidationProjectResumeModel() {
    const tunnelService = this.injector.get(TunnelService);

    const validationProjectResumeModel: StorageValidationProjectResumeModel = {
      amount: tunnelService.storageTunnelData.totalFundingCost,
    };
    this.validationProjectResume.next(validationProjectResumeModel);
  }

  private async computeInstallationPrice(): Promise<KitchenPricing> {
    const tunnelService = this.injector.get(TunnelService);
    const kitchenService = this.injector.get(KitchenService);
    const input: InstallationInput = {
      configurationID: tunnelService.kitchenTunnelData.configId,
      workbenchConfigurationProducts: tunnelService.workbenchTunnelData.resume?.products,
    };
    try {
      return await kitchenService.postInstallation(input);
    } catch (err) {
      throw new Error(`Erreur lors du calcul des prix du projet : ${err}`);
    }
  }

  private async initProjectInfo() {
    this.validationProjectInfoLoading.next(true);

    try {
      const projectService = this.injector.get(ProjectService);
      const currentService = this.injector.get(CurrentService);

      const response: any = await projectService.getDetails(currentService.HBMProject.uuid);

      let projectImages = [];

      if (response.medias.renderHQ) {
        response.medias.renderHQ.forEach(img => {
          projectImages.push({ url: img.url });
          this.projectImgUrl.push(img.url);
        });
      }

      if (response.medias.thumbnail[1].url) {
        projectImages.push({ url: response.medias.thumbnail[1].url });

        // On récupère la thumbnail seulement si on a récupéré aucune image HQ.
        if (this.projectImgUrl.length <= 0) {
          this.projectImgUrl.push(response.medias.thumbnail[1].url);
        }
      }

      const tunnelService = this.injector.get(TunnelService);
      const customerService = this.injector.get(CustomerService);

      if (this.isCurrentContextKitchen) {
        this.buildKitchenValidationProjectInfoModel(
          currentService.HBMProject.name,
          tunnelService.kitchenTunnelData.opportunityNumber,
          tunnelService.kitchenTunnelData.workbenchOpportunityNumber, //workbenchOpportunityNumber, à voir pour save la valeur en base pour la recharger
          projectImages,
          customerService.fullName,
          this.getDiscountedPriceFromKitchenPricing(true, 'prixTotal'),
          customerService.userDatas
        );
      } else {
        this.buildStorageValidationProjectInfoModel(
          currentService.HBMProject.name,
          tunnelService.storageTunnelData.opportunityNumber,
          projectImages,
          customerService.fullName,
          tunnelService.storageTunnelData.totalFundingCost,
          customerService.userDatas
        );
      }
    } catch (err) {
      logError(`Erreur lors de la récupération des détails du projet : ${err}`);
    } finally {
      this.validationProjectInfoLoading.next(false);
    }
  }

  private getDiscountedPriceFromKitchenPricing(withInstallation: boolean, attributeName: string) {
    const tunnelService = this.injector.get(TunnelService);
    if (withInstallation) {
      try {
        return tunnelService.kitchenTunnelData?.kitchenPricing['withInstallation'][this.priceKey][attributeName]
          .discountPrices[tunnelService.kitchenTunnelData?.discountValue].amount;
      } catch (err) {
        logError(`L'attribut ${attributeName} n'existe pas ! - ${err}`);
      }
    } else {
      return tunnelService.kitchenTunnelData?.kitchenPricing['withoutInstallation'][attributeName].discountPrices[
        tunnelService.kitchenTunnelData?.discountValue
      ].amount;
    }
  }

  private buildStorageValidationProjectInfoModel(
    projectName: string,
    storageOpportunityNumber: number,
    projectImages: any[],
    loggedCustomerFullName: string,
    amount: number,
    customer: Customer
  ) {
    const validationProjectInfo: StorageValidationProjectInfoModel = {
      projectName: projectName,
      opportunityNumber: storageOpportunityNumber,
      projectImages: projectImages,
      loggedCustomerFullName: loggedCustomerFullName,
      customer: customer,
      fidelityCardValidationModel: {
        amount: amount,
        customer: customer,
      },
    };

    this.validationProjectInfo.next(validationProjectInfo);
  }

  private buildKitchenValidationProjectInfoModel(
    projectName: string,
    kitchenOpportunityNumber: number,
    workbenchOpportunityNumber: number,
    projectImages: any[],
    loggedCustomerFullName: string,
    amount: number,
    customer: Customer
  ) {
    const validationProjectInfo: KitchenValidationProjectInfoModel = {
      projectName: projectName,
      opportunityNumber: kitchenOpportunityNumber,
      kitchenOpportunityNumber: kitchenOpportunityNumber,
      workbenchOpportunityNumber: workbenchOpportunityNumber,
      projectImages: projectImages,
      loggedCustomerFullName: loggedCustomerFullName,
      customer: customer,
      fidelityCardValidationModel: {
        amount: amount,
        customer: customer,
      },
    };

    this.validationProjectInfo.next(validationProjectInfo);
  }

  private buildProductList(): any[] {
    const tunnelService = this.injector.get(TunnelService);
    let factorisedProductList = [];

    //Peupler flattenBom depuis beautifiedBom, on veut factoriser chaque produit pour avoir la quantité totale de chaque produit
    this.beautifiedBom.blocks.map(block => {
      block.rows.map(product => {
        if (tunnelService.workbenchTunnelData.resume) {
          //S'il y a un plan de travail, alors on retire le plan de travail de COM+
          if (product.freeTags?.length > 0) {
            for (const freeTag of product.freeTags) {
              if (freeTag.includes('13') || freeTag.includes('14')) {
                return;
              }
            }
          }
        }

        let alreadyPopulated = factorisedProductList.find(
          factorisedProduct => factorisedProduct.reference === product.reference
        );
        if (alreadyPopulated) {
          alreadyPopulated.quantity += product.quantity;
        } else {
          let clone = { ...product};
          clone.quantity = product.quantity;
          factorisedProductList.push(clone);
        }
      });
    });

    return factorisedProductList;
  }

  async refreshComponents(componentToRefresh: VALIDATION_COMPONENT_TYPE_ENUM) {
    switch (componentToRefresh) {
      case VALIDATION_COMPONENT_TYPE_ENUM.VALIDATION_PROJECT_INFO:
        await this.initProjectInfo();
        break;
      case VALIDATION_COMPONENT_TYPE_ENUM.VALIDATION_PROJECT_RESUME:
        await this.initProjectResume();
        break;
      case VALIDATION_COMPONENT_TYPE_ENUM.VALIDATION_SIDE_BAR_ACTION:
        this.initSideBarActions();
        break;
      default:
        await this.initProjectResume();
        await this.initProjectInfo();
        this.initSideBarActions();
        break;
    }
  }
}
